import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../../styles/CustomerFollowUp.scss';
import { useParams } from 'react-router-dom';
import { OneWayInStatus } from 'src/api/v2';
import { useUser } from 'src/features/authorization';
import { toast } from 'react-toastify';
import { PageParams } from '../../types/PageParams';
import useOneWayIn from 'src/features/one-way-in/hooks/use-one-way-in';
import createOwiUpdateDto from '../../utils/createOwiUpdateDto';
import ToastContent from '../../../../../components/ToastContent/ToastContent';
import { Button, Textarea } from '@innovationnorway/cl-react';

const AddNote = () => {
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tCommon } = useTranslation();
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState(false);
  const { formId } = useParams<PageParams>();
  const { oneWayInOpportunityById, updateOneWayInOpportunityStatus } = useOneWayIn(formId);
  const { isAadUser, user, displayName } = useUser();

  const opportunity = useMemo(() => {
    if (!oneWayInOpportunityById.data) return undefined;

    return oneWayInOpportunityById.data;
  }, [oneWayInOpportunityById.data]);

  const createUpdateDto = (status: OneWayInStatus, comment: string) =>
    createOwiUpdateDto(
      opportunity?.id,
      status,
      comment,
      isAadUser ? undefined : user?.clusterUserId,
      displayName || undefined,
    );

  const onAddNote = async () => {
    if (note === '') {
      setNoteError(true);
      return;
    }
    await updateOneWayInOpportunityStatus
      .mutateAsync(createUpdateDto(OneWayInStatus.Active, note))
      .then(() => {
        toast(
          <ToastContent
            success={true}
            title={tOneWayIn('customerFollowUp.addNote.toast.success.title')}
            text={tOneWayIn('customerFollowUp.addNote.toast.success.text')}
          />,
          {
            toastId: 'NoteAdded',
            autoClose: 1000 * 15,
            position: 'bottom-center',
          },
        );
        setNote('');
      })
      .catch(() => {
        toast(
          <ToastContent
            success={false}
            title={tOneWayIn('customerFollowUp.addNote.toast.error.title')}
            text={tOneWayIn('customerFollowUp.addNote.toast.error.text')}
          />,
          {
            toastId: 'NoteError',
            autoClose: false,
            position: 'bottom-center',
          },
        );
      });
    setNoteError(false);
  };

  return (
    <div
      id="add-note"
      className="padding--3 border-radius--3 background-color--black-12 follow-up--flex-item height--fit-content stack-medium"
    >
      <h3 className="margin-block--0">{tOneWayIn('customerFollowUp.addNote.title')}</h3>
      <p>{tOneWayIn('customerFollowUp.addNote.p1')}</p>
      <ul>
        <li>{tOneWayIn('customerFollowUp.addNote.li1')}</li>
        <li>{tOneWayIn('customerFollowUp.addNote.li2')}</li>
        <li>{tOneWayIn('customerFollowUp.addNote.li3')}</li>
      </ul>
      <p>{tOneWayIn('customerFollowUp.addNote.p2')}</p>
      <div>
        <label>{tCommon('note')}</label>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
        {noteError && <span>{tOneWayIn('details.AddNote.Required')}</span>}
      </div>
      <Button
        variant="secondary"
        className="add-note--button"
        onClick={onAddNote}
        loading={updateOneWayInOpportunityStatus.isLoading}
        disabled={updateOneWayInOpportunityStatus.isLoading}
      >
        {tCommon('add')}
      </Button>
    </div>
  );
};

export default AddNote;
